import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { SentryTenantTagger } from './components/SentryTenantTagger';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AppLayout } from './components/layout/AppLayout';
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { OAuthCallbackPage } from './pages/auth/OAuthCallbackPage';
import { MagicLinkVerifyPage } from './pages/auth/MagicLinkVerifyPage';
import { MfaChallengePage } from './pages/auth/MfaChallengePage';
import { DashboardPage } from './pages/DashboardPage';
import { AccountPage } from './pages/account/AccountPage';
import { OrganizationListPage } from './pages/organizations/OrganizationListPage';
import { MembersPage } from './pages/organizations/MembersPage';
import { OrganizationSettingsPage } from './pages/organizations/OrganizationSettingsPage';
import { AuditLogPage } from './pages/organizations/AuditLogPage';
import { AcceptInvitationPage } from './pages/invitations/AcceptInvitationPage';
import { ProjectListPage } from './pages/projects/ProjectListPage';
import { ProjectDetailPage } from './pages/projects/ProjectDetailPage';
import { CreateProjectPage } from './pages/projects/CreateProjectPage';
import { EditProjectPage } from './pages/projects/EditProjectPage';
import { DocsLayout } from './pages/docs/DocsLayout';
import { DocsPage } from './pages/docs/DocsPage';

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OrganizationProvider>
          <SentryTenantTagger />
          <Routes>
            {/* Auth routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
            <Route path="/auth/magic-link/verify" element={<MagicLinkVerifyPage />} />
            <Route path="/login/mfa" element={<MfaChallengePage />} />

            {/* Invitation accept - can be accessed without auth for preview */}
            <Route path="/invite/accept" element={<AcceptInvitationPage />} />

            {/* Public docs */}
            <Route path="/docs" element={<DocsLayout />}>
              <Route index element={<DocsPage />} />
              <Route path=":slug" element={<DocsPage />} />
            </Route>

            {/* Protected routes with AppLayout */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DashboardPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/organizations"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <OrganizationListPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/organizations/:orgId"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DashboardPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/organizations/:orgId/members"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <MembersPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/organizations/:orgId/settings"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <OrganizationSettingsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/organizations/:orgId/audit-log"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AuditLogPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Project routes */}
            <Route
              path="/organizations/:orgId/projects"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProjectListPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/organizations/:orgId/projects/new"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <CreateProjectPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProjectDetailPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId/edit"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <EditProjectPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AccountPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* Legacy /security URL — redirect to the security tab on the account page. */}
            <Route path="/security" element={<Navigate to="/account?tab=security" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </OrganizationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
